$component-class: 'dh-button';
$component-icon: 'dh-icon';
$focus-ring-color: $dh-focus-ring-color;

@keyframes dh-button-loading {
  to {
    opacity: 0;
  }
}

// ── Base ──────────────────────────────────────────────────────────

.#{$component-class} {
  --_focus-inner: var(--background-color-primary);
  --_focus-shadow: 0 0 0 0 transparent;
  --_hover-shadow: 0 0 0 0 transparent;

  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  vertical-align: middle;
  text-align: center;
  text-decoration: none;
  cursor: pointer;
  user-select: none;
  touch-action: manipulation;
  -webkit-font-smoothing: antialiased;
  background-image: none;
  font-weight: map-get($font-weights, semi-bold);
  border-radius: var(--radius-size-md);
  font-size: map-get($font-sizes, md);
  line-height: map-get($line-heights, md);
  padding: calc(var(--spacing-xs) - var(--border-size-sm)) calc(var(--spacing-lg) - var(--border-size-sm));
  transition: all 0.2s ease-in-out;
  border: var(--border-size-sm) solid transparent;
  box-shadow: var(--_hover-shadow), var(--_focus-shadow);

  @include desktop {
    padding: calc(var(--spacing-sm) - var(--border-size-sm)) calc(var(--spacing-xl) - var(--border-size-sm));
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: none;

    --_focus-shadow: 0 0 0 2px var(--_focus-inner), 0 0 0 4px #{$focus-ring-color};
  }
}

// ── Full width ───────────────────────────────────────────────────

.#{$component-class}--full-width {
  width: 100%;
}

// ── Disabled ─────────────────────────────────────────────────────

.#{$component-class}--disabled,
.#{$component-class}[disabled],
.#{$component-class}[aria-disabled='true'],
.#{$component-class}:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  user-select: none;
  pointer-events: none;
}

// ── Loading ──────────────────────────────────────────────────────

.#{$component-class}--loading {
  pointer-events: none;
  user-select: none;
  cursor: wait;
  position: relative;
  color: transparent !important;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    height: auto;
    width: auto;
    animation: dh-button-loading 2s linear infinite alternate;
    border-radius: var(--radius-size-sm);
    margin: calc(var(--spacing-sm) + var(--border-size-lg)) calc(var(--spacing-xl) + var(--border-size-lg));
    background-color: unquote('color-mix(in srgb, var(--color-neutral-neutral-dark) 20%, transparent)');
  }
}

// ── Size: small ──────────────────────────────────────────────────

.#{$component-class}--sm {
  padding: calc(var(--spacing-xs) - var(--border-size-sm)) calc(var(--spacing-lg) - var(--border-size-sm));

  &.#{$component-class}--loading::before {
    margin: calc(var(--spacing-xs) + var(--border-size-lg)) calc(var(--spacing-lg) + var(--border-size-lg));
  }

  &.#{$component-class}--icon-only {
    padding: var(--spacing-xs);

    &.#{$component-class}--loading::before {
      margin: calc(var(--spacing-xs) + var(--border-size-lg));
    }
  }
}

// ── Primary ──────────────────────────────────────────────────────

.#{$component-class}--primary {
  background-color: var(--action-color-brand-primary);
  color: var(--color-neutral-white);
  border-color: var(--action-color-brand-primary);

  &:hover {
    background-color: var(--action-color-brand-primary-hover);
    border-color: var(--action-color-brand-primary-hover);
  }

  &.#{$component-class}--loading::before {
    background-color: unquote('color-mix(in srgb, var(--color-neutral-white) 15%, transparent)');
  }
}

// ── Secondary ────────────────────────────────────────────────────

.#{$component-class}--secondary {
  background-color: transparent;
  color: var(--text-color-primary);
  border-color: var(--border-color-secondary);

  &:hover {
    border-color: var(--border-color-primary);

    --_hover-shadow: inset 0 0 0 var(--border-size-sm) var(--border-color-primary);
  }
}

// ── Tertiary ─────────────────────────────────────────────────────

.#{$component-class}--tertiary {
  background-color: var(--action-color-primary);
  color: var(--text-color-alternate);
  border-color: var(--action-color-primary);

  &:hover {
    background-color: var(--action-color-primary-hover);
    border-color: var(--action-color-primary-hover);
  }

  &:focus-visible {
    background-color: var(--action-color-primary-hover);
    border-color: var(--action-color-primary-hover);
  }
}

// ── Link ─────────────────────────────────────────────────────────

.#{$component-class}--link {
  border: 0;
  padding: 0;
  border-radius: 0;
  background-color: transparent;
  color: var(--link-color-primary);

  .#{$component-class}__text {
    position: relative;

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 0;
      height: var(--border-size-md);
      background-color: currentcolor;
      transition: width 0.2s ease-in-out;
    }
  }

  &:hover {
    color: var(--action-color-primary-hover);

    .#{$component-class}__text::after {
      width: 100%;
    }
  }

  &:focus-visible .#{$component-class}__text::after {
    width: 0;
  }

  &.#{$component-class}--icon-only {
    padding: 0;

    &:hover {
      color: var(--action-color-primary-hover);
    }
  }

  &.#{$component-class}--loading::before {
    margin: var(--border-size-lg);
  }
}

// ── Icon positioning ─────────────────────────────────────────────

.#{$component-class}--icon-before {
  .#{$component-icon} {
    margin-right: var(--spacing-sm);
  }
}

.#{$component-class}--icon-after {
  .#{$component-icon} {
    margin-left: var(--spacing-sm);
  }
}

.#{$component-class}--icon-only {
  padding: var(--spacing-sm);

  .#{$component-icon} {
    @include size(1.5rem);
  }

  &.#{$component-class}--loading::before {
    margin: calc(var(--spacing-sm) + var(--border-size-lg));
  }
}
